import { z } from 'zod';

/**
 * Which server this is (vitrine.md §2): the answer of `GET /api/instance`.
 *
 * A server has ONE role, set by `FORM_ROLE` at boot. The `atelier` is the dev,
 * the workstation, the CI and the e2e stack (the default): versions are born
 * there and everything is edited there. The `vitrine` is production: it plays
 * what the atelier sent it, receives the participants and edits no model.
 */
export const instanceRoleSchema = z.enum(['atelier', 'vitrine']);
export type InstanceRole = z.infer<typeof instanceRoleSchema>;
export const INSTANCE_ROLES = instanceRoleSchema.options;

/**
 * The body of `GET /api/instance` (vitrine.md §2), public.
 *
 * - `role`: `FORM_ROLE`.
 * - `code`: the version of the code - the `vX.Y.Z` tag when the image comes
 *   from a tag, else the short SHA of `main`, else `local`.
 * - `origin`: the public origin (`PUBLIC_ORIGIN`), or null when the server was
 *   not told its own address.
 * - `atelierOrigin`: where the ATELIER is (`ATELIER_ORIGIN`), posed on the
 *   vitrine only - the address the banner « Ouvrir sur form.dev » sends the
 *   operator to (§7). Null on the atelier, and on a vitrine that was not told.
 * - `prodOrigin`: where the PROD is (`PROD_ORIGIN`), posed on the atelier only
 *   (issue #199, interruption.md §5) - the address the « l'ouvrir sur la prod »
 *   link of a session born there sends the operator to. Null on the vitrine, and
 *   on an atelier that was not told.
 *
 * The front reads it at the start of the admin to choose its screens, the dev
 * reads it on the vitrine for its banner (§9.3), and `smoke:prod` probes it to
 * prove it talks to the right production (§9.4).
 */
export const instanceSchema = z
  .object({
    role: instanceRoleSchema,
    code: z.string().min(1),
    origin: z.string().nullable(),
    atelierOrigin: z.string().nullable(),
    prodOrigin: z.string().nullable(),
  })
  .strict();
export type Instance = z.infer<typeof instanceSchema>;

/**
 * The same answer as ANOTHER server's, read by a client (issue #192): tolerant
 * where `instanceSchema` is strict.
 *
 * `instanceSchema` is what OUR route serves, so it is strict and complete. The
 * atelier also reads the instance of the vitrine, whose code may be OLDER (a
 * v1.3.x with no `atelierOrigin`) or NEWER (a field this code does not know),
 * and refusing either would make the atelier fail on `ProdInvalidResponseError`
 * for a difference that is not an error. So a reader of another server's answer
 * defaults an absent origin to `null` and drops an unknown field - and still
 * insists on a known `role` and a `code`, the two things it cannot guess.
 */
export const instanceReadSchema = z.object({
  role: instanceRoleSchema,
  code: z.string().min(1),
  origin: z.string().nullable().default(null),
  atelierOrigin: z.string().nullable().default(null),
  prodOrigin: z.string().nullable().default(null),
});
